import { useMutation } from '@apollo/client/react';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

import {
  CreateFollowupMessageDocument,
  UpdateFollowupMessageDocument,
} from '~/graphql/generated/graphql';
import { useMedicationFollowupQuestionnaires } from '~/hooks/use-medication-followup-questionnaires';
import {
  eventPageInfoState,
  patientDetailDialogMessageState,
} from '~/state/partials/patient_detail_dialog/atoms';
import { formatISOString, toYYYYMMDDhyphen } from '~/utils/date';

import { Fields } from '../MessageForm/InputPane/types';
import { useBuildRefetchQueries } from './use-build-refetch_queries';

export const useSendMessage = () => {
  const refetchQueries = useBuildRefetchQueries();
  const { questionnaires } = useMedicationFollowupQuestionnaires();
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [{ profile }, setState] = useAtom(patientDetailDialogMessageState);

  const appointmentId = profile?.type !== 'DirectVisitor' ? profile?.appointmentId : undefined;
  const patientId = profile?.type === 'Patient' ? profile?.patientId : undefined;
  const guestPatientId = profile?.type !== 'Patient' ? profile?.guestPatientId : undefined;

  const setEventInfoState = useSetAtom(eventPageInfoState);
  const [create, { data: createdData }] = useMutation(CreateFollowupMessageDocument, {
    refetchQueries,
  });
  const [update, { data: updatedData }] = useMutation(UpdateFollowupMessageDocument, {
    refetchQueries,
  });

  useEffect(() => {
    if (
      !createdData?.createMedicationFollowupMessage2 &&
      !updatedData?.updateMedicationFollowupMessage2
    ) {
      return;
    }
    setEventInfoState((_state) => ({ ..._state, isResetEvent: true, page: 1, perPage: 20 }));
  }, [createdData, setEventInfoState, updatedData]);

  const send = useCallback(
    async (values: Fields, messageId: string | null, isSendingFailed?: boolean) => {
      if (!patientId && !guestPatientId) return;
      if (values.attachment === 'clinic' && values.csClinicId.length === 0) {
        setError('病院または、診療所を選択してください');
        return;
      }

      setIsSending(true);
      setError(null);

      const sendAt =
        values.fixed && values.sendDate
          ? formatISOString(`${toYYYYMMDDhyphen(values.sendDate)} ${values.sendTime}:00`)
          : null;

      const medicationFollowupQuestionnaireId = values.medicationFollowupQuestionnaireId;
      const medicationFollowupQuestionnaireRevisionId =
        values.attachment === 'questionnaire_sheet' && medicationFollowupQuestionnaireId
          ? questionnaires?.find((q) => q.id === medicationFollowupQuestionnaireId)?.revision.id
          : null;
      const hasQuestionnaireSheetWithAutoReplySetting =
        values.attachment === 'questionnaire_sheet' && values.isEnabledAutoReplySetting;
      const eventId = values.medixsEventId ?? null;

      try {
        if (messageId && !isSendingFailed) {
          await update({
            variables: {
              input: {
                sendAt,
                medicationFollowupMessageId: messageId,
                message: values.message,
                csClinicId: values.attachment === 'clinic' ? values.csClinicId : null,
                medicationFollowupQuestionnaireRevisionId:
                  medicationFollowupQuestionnaireRevisionId,
                autoReplyMessage: hasQuestionnaireSheetWithAutoReplySetting
                  ? values.autoReplyMessage
                  : null,
                withAutoReplyQuestionnaire: hasQuestionnaireSheetWithAutoReplySetting
                  ? values.withAutoReplyQuestionnaire
                  : null,
                eventId,
                startingPoint: values.startingPoint,
              },
            },
          });
        } else {
          await create({
            variables: {
              input: {
                appointmentId,
                guestPatientId,
                patientId,
                sendAt,
                message: values.message,
                csClinicId: values.attachment === 'clinic' ? values.csClinicId : null,
                medicationFollowupQuestionnaireRevisionId:
                  medicationFollowupQuestionnaireRevisionId,
                autoReplyMessage: hasQuestionnaireSheetWithAutoReplySetting
                  ? values.autoReplyMessage
                  : null,
                withAutoReplyQuestionnaire: hasQuestionnaireSheetWithAutoReplySetting
                  ? values.withAutoReplyQuestionnaire
                  : null,
                eventId,
                startingPoint: values.startingPoint,
              },
            },
          });
        }
        setIsSending(false);
        setState((_state) => ({
          ..._state,
          selectedMessageId: null,
          messageInfoStatus: sendAt ? 'sendAt' : 'created',
        }));
      } catch (_error) {
        setError(_error?.message || 'エラーが発生しました');
        setIsSending(false);
        setState((_state) => ({
          ..._state,
          messageInfoStatus: 'new',
          messageInfoIsOpen: false,
        }));
        throw new Error('', { cause: _error });
      }
    },
    [patientId, guestPatientId, questionnaires, setState, update, create, appointmentId],
  );

  return {
    sending: isSending,
    error,
    send,
  };
};
